import { useCallback, useEffect, useState } from "react";
import { ProfileCreationModal } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AutoProfileCreationCard/ProfileCreationModal";
import ProfileMethodSelector from "~/pages/Compliance/profile/ProfileCreator/ProfileMethodSelector";
import { toDbId } from "~/utils/graphql-helpers";
import { useEntityClientWizard } from "../context";
import { EntityProfileForm } from "./EntityProfileForm";
import { ProfileExtractionPreview } from "./ProfileExtractionPreview";

type ProfileSubStep = "method" | "manual" | "extraction-preview";

const getDraftSubStep = (hasDraft: boolean, isPrefilled?: boolean, documentId?: number) => {
  if (documentId && isPrefilled) return "extraction-preview" as const;
  return hasDraft ? ("manual" as const) : ("method" as const);
};

export const ProfileStep = () => {
  const { goBack, profileDraft, setFooterContent, caseInfo } = useEntityClientWizard();
  const [subStep, setSubStep] = useState<ProfileSubStep>(() =>
    getDraftSubStep(Boolean(profileDraft), profileDraft?.isPrefilled, profileDraft?.documentId),
  );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>(profileDraft?.documentId);

  const arenaId = caseInfo?.clientId ? toDbId(caseInfo.clientId) : undefined;

  useEffect(() => {
    if (subStep !== "method") return;
    setFooterContent(null);
  }, [setFooterContent, subStep]);

  const handleExtractionSuccess = useCallback((docId: number) => {
    setDocumentId(docId);
    setShowUploadModal(false);
    setSubStep("extraction-preview");
  }, []);

  if (subStep === "extraction-preview") {
    return documentId ? (
      <ProfileExtractionPreview documentId={documentId} onBack={() => setSubStep("method")} />
    ) : null;
  }

  if (subStep === "manual") {
    return <EntityProfileForm onBack={() => setSubStep("method")} />;
  }

  return (
    <>
      <ProfileMethodSelector
        backLabel="Case information"
        onBack={goBack}
        onSelectManual={() => setSubStep("manual")}
        onSelectExtraction={() => setShowUploadModal(true)}
      />
      {showUploadModal && (
        <ProfileCreationModal
          arenaId={arenaId}
          onClose={() => setShowUploadModal(false)}
          onManualCreate={() => setSubStep("manual")}
          onExtractionSuccess={handleExtractionSuccess}
        />
      )}
    </>
  );
};
